/**
 * usePolling — silent polling for "real-time" UI feel.
 *
 * Calls a fetcher function on a configurable interval. Pauses when the tab
 * is hidden (via the Page Visibility API) so we don't burn battery, and
 * resumes — and immediately re-fetches — when the tab becomes visible
 * again. The fetcher is called once on mount.
 *
 * Re-render guard:
 *   The fetcher should call setState only when data has actually changed.
 *   Helpers below give callers a `dedupeAndSet(prev => next)` pattern so
 *   they don't trigger needless React re-renders when the JSON is identical.
 *
 * Usage:
 *   const tickRef = useRef(null);
 *   useEffect(() => { tickRef.current = doFetch; });
 *   const fetcher = useCallback(() => tickRef.current?.(), []);
 *   usePolling(fetcher, POLL_INTERVALS.dashboard);
 *
 * Or, more simply, pass a stable useCallback fetcher.
 */

import { useEffect, useRef } from 'react';

/**
 * Default polling intervals (ms). Tune here.
 *
 * IMPORTANT: keep these conservative — too aggressive == backend pressure.
 *  - dashboard: every 30s
 *  - unifiedOrders: every 30s (page 1 only)
 *  - productionBoard: every 60s
 *  - whatsappThreads: every 20s (chat-like → faster)
 *  - sidebarBadges: every 60s (low-priority counters)
 */
export const POLL_INTERVALS = {
  dashboard: 30 * 1000,
  unifiedOrders: 30 * 1000,
  productionBoard: 60 * 1000,
  whatsappThreads: 20 * 1000,
  sidebarBadges: 60 * 1000,
};

/**
 * Stable shallow-equals via JSON.stringify. Cheap enough for the smallish
 * payloads we poll (< 50KB). Returns true when prev === next (deep value).
 */
export const isSameJson = (prev, next) => {
  try {
    return JSON.stringify(prev) === JSON.stringify(next);
  } catch (_) {
    return false;
  }
};

/**
 * usePolling
 *
 * @param {Function} fetcher  async () => void — your fetch logic. The hook
 *                            does NOT pass anything to it; close over your
 *                            own deps via useCallback.
 * @param {number}  intervalMs Polling cadence. Pass 0 / falsy to disable.
 * @param {Array}   deps      Re-arm the interval when these change.
 * @param {Object}  [opts]
 * @param {boolean} [opts.runOnMount=true]   Fire fetcher immediately on mount.
 * @param {boolean} [opts.pauseWhenHidden=true] Stop ticking when tab is hidden.
 *
 * @returns {{ stop: () => void }} Imperative stop handle (rarely needed).
 */
export function usePolling(fetcher, intervalMs, deps = [], opts = {}) {
  const { runOnMount = true, pauseWhenHidden = true } = opts;
  const fetcherRef = useRef(fetcher);
  const intervalRef = useRef(null);
  const stoppedRef = useRef(false);

  // Always call the latest fetcher without re-arming the interval on every
  // render — this is the standard "stable poll" pattern.
  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  useEffect(() => {
    stoppedRef.current = false;
    if (!intervalMs) return undefined;

    const tick = () => {
      if (stoppedRef.current) return;
      // Skip when hidden — saves battery and stops needless backend hits.
      if (pauseWhenHidden && typeof document !== 'undefined' && document.hidden) {
        return;
      }
      try {
        const result = fetcherRef.current?.();
        if (result && typeof result.catch === 'function') {
          result.catch(() => { /* swallow — caller logs */ });
        }
      } catch (_) {
        /* swallow — caller logs */
      }
    };

    const start = () => {
      if (intervalRef.current) return;
      intervalRef.current = setInterval(tick, intervalMs);
    };

    const stop = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    // Visibility-aware: when tab is hidden, stop the interval entirely.
    // When it returns to visible, immediately re-fetch (so the user sees
    // fresh data without waiting for the next tick) and resume ticking.
    const onVisibility = () => {
      if (!pauseWhenHidden) return;
      if (document.hidden) {
        stop();
      } else {
        tick();
        start();
      }
    };

    if (runOnMount) tick();

    if (!pauseWhenHidden || (typeof document !== 'undefined' && !document.hidden)) {
      start();
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }

    return () => {
      stoppedRef.current = true;
      stop();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, pauseWhenHidden, runOnMount, ...deps]);

  return {
    stop: () => {
      stoppedRef.current = true;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    },
  };
}

export default usePolling;
